import { ContentAccounts, ContentCount, ContentStrategy, WeekDays } from 'types'
import { pluralize } from 'utils'
import { ContentCategory, ContentTypeCounts, DEFAULT_CONTENT_TYPE_COUNTS } from 'utils/brandPlans/contentCategories'
import { ContentTypeSettingsMap } from './Types'

export const CATEGORY_TO_ACCOUNTS_KEY: Record<ContentCategory, keyof ContentAccounts> = {
  still_image_post: 'still_image',
  carousel_post: 'carousel',
  feed_video_post: 'video',
  short_form_video: 'short',
  avatar_video: 'avatar_video',
  stories: 'story',
  blog_post: 'blog_post',
  newsletter: 'newsletter',
}

export function buildContentAccounts(
  accountIdsByCategory: Partial<Record<ContentCategory, number[]>>,
): ContentAccounts {
  return Object.fromEntries(
    (Object.entries(CATEGORY_TO_ACCOUNTS_KEY) as [ContentCategory, keyof ContentAccounts][]).map(([category, key]) => [
      key,
      accountIdsByCategory[category] ?? [],
    ]),
  ) as unknown as ContentAccounts
}

const sumCounts = (items: ContentCount[] | undefined, filter?: (item: ContentCount) => boolean): number =>
  (items ?? []).filter(filter ?? (() => true)).reduce((sum, item) => sum + (item.count ?? 0), 0)

const sumByContentType = (items: ContentCount[] | undefined, contentType: string): number =>
  sumCounts(items, (item) => item.content_type === contentType)

export const deriveInitialCountsFromStrategy = (config: ContentStrategy | null): ContentTypeCounts => {
  if (!config) return { ...DEFAULT_CONTENT_TYPE_COUNTS }

  const crosspost = config.crosspost_contents
  const untypedCrosspost = sumCounts(crosspost, (item) => item.type === 'post' && !item.content_type)

  return {
    still_image_post: sumByContentType(crosspost, 'still_image') + untypedCrosspost,
    carousel_post: sumByContentType(crosspost, 'carousel'),
    feed_video_post: sumByContentType(crosspost, 'video'),
    short_form_video: sumCounts(config.video_crosspost_contents, (item) => item.content_type !== 'avatar_video'),
    avatar_video: sumByContentType(config.video_crosspost_contents, 'avatar_video'),
    stories: sumCounts(config.instagram_contents, (item) => item.type === 'story'),
    blog_post: sumCounts(config.seo_contents),
    newsletter: sumCounts(config.email_contents),
  }
}

export const buildLabelFromCounts = (config: ContentTypeSettingsMap | null): string => {
  if (!config) return 'No content types selected'

  const still_image_post = config.still_image_post.count
  const carousel_post = config.carousel_post.count
  const feed_video_post = config.feed_video_post.count
  const short_form_video = config.short_form_video.count
  const avatar_video = config.avatar_video.count
  const video = feed_video_post + short_form_video + avatar_video
  const stories = config.stories.count
  const blog_post = config.blog_post.count
  const newsletter = config.newsletter.count

  const parts = [
    still_image_post > 0 ? `${still_image_post} ${pluralize('still', still_image_post)}` : '',
    carousel_post > 0 ? `${carousel_post} ${pluralize('carousel', carousel_post)}` : '',
    video > 0 ? `${video} ${pluralize('video', video)}` : '',
    stories > 0 ? `${stories} ${pluralize('story', stories)}` : '',
    blog_post > 0 ? `${blog_post} ${pluralize('blog', blog_post)}` : '',
    newsletter > 0 ? `${newsletter} ${pluralize('email', newsletter)}` : '',
  ].filter(Boolean)

  return parts.length > 0 ? parts.join(', ') : 'No content types selected'
}

export const deriveInitialSettingsFromCounts = (
  counts: ContentTypeCounts,
  defaultWeekDays: WeekDays,
): ContentTypeSettingsMap => {
  const settingFor = (category: keyof ContentTypeSettingsMap) => ({
    count: counts[category],
    weekDays: defaultWeekDays,
  })

  return {
    still_image_post: settingFor('still_image_post'),
    carousel_post: settingFor('carousel_post'),
    feed_video_post: settingFor('feed_video_post'),
    short_form_video: settingFor('short_form_video'),
    avatar_video: settingFor('avatar_video'),
    stories: settingFor('stories'),
    newsletter: settingFor('newsletter'),
    blog_post: settingFor('blog_post'),
  }
}

export const buildContentsFromSettings = (settings: ContentTypeSettingsMap) => {
  const crosspostContents: ContentCount[] = []
  const videoContents: ContentCount[] = []
  const instagramContents: ContentCount[] = []
  const seoContents: ContentCount[] = []
  const emailContents: ContentCount[] = []

  const s = settings.still_image_post
  if (s.count > 0) {
    crosspostContents.push({
      type: 'post',
      count: s.count,
      content_type: 'still_image',
      week_days: s.weekDays.length > 0 ? s.weekDays : undefined,
    })
  }
  const sc = settings.carousel_post
  if (sc.count > 0) {
    crosspostContents.push({
      type: 'post',
      count: sc.count,
      content_type: 'carousel',
      week_days: sc.weekDays.length > 0 ? sc.weekDays : undefined,
    })
  }
  const sv = settings.feed_video_post
  if (sv.count > 0) {
    crosspostContents.push({
      type: 'post',
      count: sv.count,
      content_type: 'video',
      week_days: sv.weekDays.length > 0 ? sv.weekDays : undefined,
    })
  }
  const sfv = settings.short_form_video
  if (sfv.count > 0) {
    videoContents.push({
      type: 'post',
      count: sfv.count,
      week_days: sfv.weekDays.length > 0 ? sfv.weekDays : undefined,
    })
  }
  const av = settings.avatar_video
  if (av.count > 0) {
    videoContents.push({
      type: 'post',
      count: av.count,
      content_type: 'avatar_video',
      week_days: av.weekDays.length > 0 ? av.weekDays : undefined,
    })
  }
  const st = settings.stories
  if (st.count > 0) {
    instagramContents.push({
      type: 'story',
      count: st.count,
      week_days: st.weekDays.length > 0 ? st.weekDays : undefined,
    })
  }
  const bp = settings.blog_post
  if (bp.count > 0) {
    seoContents.push({
      type: 'blog_post',
      count: bp.count,
      week_days: bp.weekDays.length > 0 ? bp.weekDays : undefined,
    })
  }
  const nl = settings.newsletter
  if (nl.count > 0) {
    emailContents.push({
      type: 'newsletter',
      count: nl.count,
      week_days: nl.weekDays.length > 0 ? nl.weekDays : undefined,
    })
  }

  return {
    crosspost_contents: crosspostContents,
    video_crosspost_contents: videoContents,
    instagram_contents: instagramContents,
    seo_contents: seoContents,
    email_contents: emailContents,
  }
}
